/**
 * Google Analytics 4 — transport layer.
 *
 * Clarity answers "why did this person leave" by showing the recording. GA4
 * answers "how many, from where, and when" with numbers Clarity simply does not
 * produce. They overlap almost nowhere, which is why both are wired to the same
 * events through utils/analytics.js rather than one being chosen over the other.
 *
 * The tag itself lives in public/index.html.
 *
 * Same two rules as the Clarity wrapper: never throw (ad blockers remove gtag
 * far more often than they remove Clarity), and never send personal data. GA4's
 * own terms prohibit sending PII, and an account could be terminated for it — so
 * user ids go to `user_id`, which is the field Google provides for exactly this,
 * and names, emails and phone numbers are never sent at all.
 */

const isEnabled = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

const send = (...args) => {
  if (!isEnabled()) return false;
  try {
    window.gtag(...args);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * GA4 rejects event names over 40 characters and anything that is not
 * alphanumeric/underscore, dropping the event silently rather than erroring.
 * Normalising here means a bad name shows up as a wrong-looking event instead of
 * a missing one.
 */
const safeName = (name) =>
  String(name || '')
    .trim()
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .slice(0, 40);

/** Parameter values are capped at 100 characters by GA4. */
const safeParams = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    out[safeName(key)] = typeof value === 'number' ? value : String(value).slice(0, 100);
  });
  return out;
};

export const trackEvent = (name, params = {}) => {
  const eventName = safeName(name);
  if (!eventName) return false;
  return send('event', eventName, safeParams(params));
};

/**
 * Page view for a client-side route change.
 *
 * index.html configures the tag with send_page_view: false, so every page view
 * — including the first — comes from here. Left on the default, this SPA would
 * have reported exactly one page view per visit: the landing URL. Every
 * subsequent navigation happens in the router and never reloads the document,
 * so gtag would never see it.
 */
export const trackPageView = (path, title) =>
  send('event', 'page_view', safeParams({
    page_path: path,
    page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    page_title: title || (typeof document !== 'undefined' ? document.title : undefined)
  }));

/**
 * Associate the session with an account.
 *
 * `user_id` is Google's supported field for a stable, non-personal identifier —
 * the Mongo id. Putting an email here would be a terms violation, not just poor
 * practice.
 */
export const setUserId = (userId) => {
  if (!userId) return false;
  return send('set', { user_id: String(userId) });
};

/** Role etc. as a user property, so GA4 reports can be segmented by it. */
export const setUserProperty = (key, value) => {
  if (!key || value === undefined || value === null || value === '') return false;
  return send('set', 'user_properties', { [safeName(key)]: String(value).slice(0, 36) });
};

const ga = { isEnabled, trackEvent, trackPageView, setUserId, setUserProperty };

export default ga;
